import { path } from './client'
import type { ApiClient, Query } from './client'
import type {
  KnowledgeDocument,
  KnowledgeStatus,
  KnowledgeVersion,
  KnowledgeVersionSummary,
  Locale,
} from './types'

/**
 * What Kura is allowed to say, and what it must not say whatever else it knows.
 *
 * The whole product claim rests on this module: an organization's answer comes
 * from what that organization approved, and it stays attributable a month
 * later. Two things follow, and both shape the API rather than just the UI.
 *
 * A document is never edited in place. `addVersion` writes a new frozen state
 * and `publish` points the document at one. That is why the version list is an
 * endpoint of its own and not a field: the version id is recorded on the
 * conversation that used it, and "why did it say that" is answered by fetching
 * the exact text that was live at the time, not the text that is live now.
 *
 * **A document is addressed by its own id and not under its project.** Every
 * call here except the two that address a project outright, list and create,
 * goes to `/v1/org/knowledge/:documentId`. This module used to nest all of them
 * under `/v1/org/projects/:projectId/knowledge/...`, which no route serves, so
 * opening any document in the console produced a 404 and the screen showed
 * nothing. The server's shape is also the better one: a path carrying both ids
 * is a path where the two can disagree, and somebody then has to decide what
 * `/projects/A/knowledge/<a document of project B>` means.
 *
 * **Archiving is not deleting.** The console sent `DELETE`, and the server has
 * no delete for knowledge at all, deliberately: a hard delete would cascade the
 * versions away and the versions are the audit trail that makes an answer
 * attributable. `archive` is a `POST`, and it takes guidance out of Kura's
 * mouth while leaving everything it ever said on the record.
 */

export interface KnowledgeListQuery extends Query {
  status?: KnowledgeStatus
  locale?: Locale
  /** A fragment of the title. Matched case-insensitively by the server. */
  q?: string
  limit?: number
  cursor?: string
}

export interface KnowledgeInput {
  title: string
  locale: Locale
  body: string
  /**
   * Why this text exists, recorded on the version rather than on the document.
   *
   * It is written at the moment of the edit and not at the moment of
   * publication, which is where the console used to ask for it. Publishing
   * takes a version that already exists, so a note collected there had nowhere
   * to be stored and was dropped.
   */
  changeNote?: string
  effectiveFrom?: string | null
  /**
   * When the guidance stops being true.
   *
   * The field exists because a distribution date passes and the advice becomes
   * wrong while the document still looks fine. Guidance with an end date stops
   * being said on its own, instead of waiting for somebody to remember.
   */
  effectiveUntil?: string | null
}

/** A fresh state of the text. The title is left out to keep the one it has. */
export interface KnowledgeVersionInput {
  title?: string
  body: string
  changeNote?: string
}

/**
 * The operational fields, changed without writing a version.
 *
 * Deliberately no `title` or `body`: changing what a document says writes a
 * version, because it has to leave a record. `status` accepts the two writing
 * states and nothing else, so publishing cannot be reached by sending a string
 * to the endpoint that only needs permission to write.
 */
export interface KnowledgePatch {
  status?: Extract<KnowledgeStatus, 'draft' | 'in_review'>
  effectiveFrom?: string | null
  effectiveUntil?: string | null
}

/** A document and the version it points at, which is how the server pairs them. */
export interface KnowledgeDetail {
  document: KnowledgeDocument
  /** Null for a document with no version, which nothing in the product creates. */
  version: KnowledgeVersion | null
}

export interface KnowledgeWrite {
  document: KnowledgeDocument
  version: KnowledgeVersion
}

export interface KnowledgeVersionWrite extends KnowledgeWrite {
  /**
   * Whether the version just written is the one Kura is given.
   *
   * True while a document is still a draft and false once it is published,
   * because an edit to published guidance is a proposal until somebody
   * publishes it. An editor who is not told that believes their correction
   * went out.
   */
  isCurrent: boolean
}

export function knowledgeApi(http: ApiClient) {
  /**
   * What Kura refuses for this project, whatever the knowledge contains.
   *
   * **This is a field on the project and not a resource of its own**, which is
   * the decision worth writing down. The console previously called `GET`,
   * `POST` and `DELETE` on `/v1/org/projects/:id/must-not-say`, three routes
   * that have never existed, and modelled each phrase as a row with an id, a
   * reason and a creation date. The server stores `must_not_say` as a text
   * array on `projects`, returned by `GET /v1/org/projects/:id` and set by
   * `PATCH`.
   *
   * The server is right and the console moves, for three reasons rather than
   * deference. The list is read in full on every session open and composed into
   * the model instructions in order, so it is used as a whole and never one
   * phrase at a time. A prohibition has no lifecycle to speak of, no status, no
   * approval and no history, so an id would identify something nothing else
   * ever refers to. And the array is bounded at two hundred entries by the
   * route, which is what makes replacing it wholesale honest rather than a way
   * to lose somebody else's edit at scale.
   *
   * What is genuinely given up, and it is not nothing: the reason a phrase was
   * added. A list of bare prohibitions is a list nobody dares remove anything
   * from, because whoever added it has left and nothing says whether it was a
   * legal requirement or somebody's Tuesday. Keeping the reason needs a column
   * that does not exist, and the one place it must not be put instead is inside
   * the phrase, because the phrase is fed to the model as an instruction.
   *
   * The read is deliberately the whole project rather than a projection. There
   * is no endpoint that returns this field alone and asking for one would be a
   * fourth route for a string array.
   */
  const prohibitions = {
    async list(projectId: string): Promise<string[]> {
      const project = await http.request<{ mustNotSay?: unknown }>(
        path`/v1/org/projects/${projectId}`,
      )
      return asPhrases(project.mustNotSay)
    },

    /**
     * Sets the whole list, because the column is the whole list.
     *
     * The caller sends what it read plus or minus one phrase, so two people
     * editing this at once means the later save wins outright rather than
     * merging. That is the cost of a column over a table, and at a couple of
     * dozen phrases per project, edited perhaps twice a year, it is the right
     * side of the trade.
     */
    async replace(projectId: string, phrases: readonly string[]): Promise<string[]> {
      const project = await http.request<{ mustNotSay?: unknown }>(
        path`/v1/org/projects/${projectId}`,
        { method: 'PATCH', body: { mustNotSay: [...phrases] } },
      )
      return asPhrases(project.mustNotSay)
    },
  }

  return {
    list(projectId: string, query: KnowledgeListQuery = {}) {
      return http.list<KnowledgeDocument>(path`/v1/org/projects/${projectId}/knowledge`, query)
    },

    create(projectId: string, input: KnowledgeInput) {
      return http.request<KnowledgeWrite>(path`/v1/org/projects/${projectId}/knowledge`, {
        method: 'POST',
        body: input,
      })
    },

    get(documentId: string) {
      return http.request<KnowledgeDetail>(path`/v1/org/knowledge/${documentId}`)
    },

    /** Writes a new version. What Kura is given does not change until publish. */
    addVersion(documentId: string, input: KnowledgeVersionInput) {
      return http.request<KnowledgeVersionWrite>(path`/v1/org/knowledge/${documentId}/versions`, {
        method: 'POST',
        body: input,
      })
    },

    /** The effective window and the draft status. Never the words. */
    update(documentId: string, input: KnowledgePatch) {
      return http.request<{ document: KnowledgeDocument }>(path`/v1/org/knowledge/${documentId}`, {
        method: 'PATCH',
        body: input,
      })
    },

    /**
     * Makes a version the one Kura is given. Absent means the newest.
     *
     * There is no note to send. What changed is written on the version when it
     * is saved, which is the only place the server keeps it.
     */
    publish(documentId: string, versionId?: string) {
      return http.request<KnowledgeWrite>(path`/v1/org/knowledge/${documentId}/publish`, {
        method: 'POST',
        body: versionId === undefined ? {} : { versionId },
      })
    },

    /**
     * Stops Kura saying this, and keeps every word of it.
     *
     * A `POST` to its own path rather than a `DELETE` on the document. The
     * distinction is the point: nothing is removed, so the conversations that
     * cited a version of this document stay answerable a year from now.
     */
    archive(documentId: string) {
      return http.request<{ document: KnowledgeDocument }>(
        path`/v1/org/knowledge/${documentId}/archive`,
        { method: 'POST' },
      )
    },

    /** Every state this document has had, newest first, without the bodies. */
    versions(documentId: string, query: Query = {}) {
      return http.list<KnowledgeVersionSummary>(
        path`/v1/org/knowledge/${documentId}/versions`,
        query,
      )
    },

    /** One version whole, including the text. For reading back what was said. */
    version(documentId: string, versionId: string) {
      return http.request<{ version: KnowledgeVersion }>(
        path`/v1/org/knowledge/${documentId}/versions/${versionId}`,
      )
    },

    prohibitions,
  }
}

/**
 * Reads the field, or reports that the project has no prohibitions.
 *
 * A 200 whose body is missing a field is a thing real servers do, through a
 * proxy that rewrote it or a version skew between this build and the one
 * deployed, and it crashes a screen exactly as a 404 does while being far
 * harder to see. An empty list is the safe reading here: the screen says the
 * project refuses nothing in particular, which is what an empty column means
 * anyway, rather than throwing while rendering a settings page.
 */
function asPhrases(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string')
}

export type KnowledgeApi = ReturnType<typeof knowledgeApi>
